import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { avatarOf, customAvatarSrc } from "../lib/avatars";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Camera, Trash2, Check, Palette, User } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import ShareCard from "../components/ShareCard";

// Downscale an image file to a ~512px square-ish JPEG before upload so the
// payload stays small and fast on mobile networks (avatars don't need to be
// full-res, and it avoids proxy size limits).
function downscale(file, max = 512, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("resize failed"))),
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
    img.src = url;
  });
}

function initialsOf(name) {
  const raw = (name || "").trim();
  if (!raw) return "?";
  const local = raw.includes("@") ? raw.split("@")[0] : raw;
  const tokens = local.split(/[\s._-]+/).filter(Boolean);
  if (tokens.length === 0) return raw[0].toUpperCase();
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
}

export default function Profile() {
  const { t } = useTranslation();
  const { user, profile, setProfile } = useAuth();
  const fileRef = useRef(null);
  const [name, setName] = useState(profile?.display_name || user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [uploading, setUploading] = useState(false);

  const photo = customAvatarSrc(profile);
  const presetImg = profile?.selected_avatar ? avatarOf(profile).img : null;
  const displayName = profile?.display_name || user?.name || user?.email || t("hud.player");

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error(t("profile.name_empty")); return; }
    setSavingName(true);
    try {
      const { data } = await api.post("/profile/display-name", { name: trimmed });
      setProfile(data);
      toast.success(t("profile.name_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("profile.save_failed"));
    } finally {
      setSavingName(false);
    }
  };

  const onPickPhoto = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error(t("profile.pick_image")); return; }
    setUploading(true);
    try {
      const blob = await downscale(file);
      const form = new FormData();
      form.append("file", blob, "avatar.jpg");
      const { data } = await api.post("/profile/avatar-photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setProfile(data);
      toast.success(t("profile.photo_saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("profile.upload_failed"));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      const { data } = await api.delete("/profile/avatar-photo");
      setProfile(data);
      toast.success(t("profile.photo_removed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("profile.save_failed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto space-y-8" data-testid="profile-page">
      <div>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-4xl font-light flex items-center gap-3">
          <User strokeWidth={1.4} /> {t("profile.title")}
        </h1>
        <p className="text-[#2C332A]/60 mt-1 text-sm">{t("profile.subtitle")}</p>
      </div>

      {/* Avatar */}
      <section className="bg-white rounded-3xl border border-[#E6E2D8] p-6 flex flex-col items-center gap-4">
        <div
          data-testid="profile-avatar"
          className="w-28 h-28 rounded-full ring-4 ring-[#F0EDE3] shadow-md overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#5F7161] to-[#4D5D4F]"
        >
          {photo ? (
            <img src={photo} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar-photo" />
          ) : presetImg ? (
            <img src={presetImg} alt="avatar" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white text-3xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
              {initialsOf(displayName)}
            </span>
          )}
        </div>

        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-photo-input" />
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            data-testid="profile-upload-btn"
            onClick={onPickPhoto}
            disabled={uploading}
            className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
          >
            <Camera size={16} strokeWidth={1.8} className="mr-1.5" />
            {uploading ? t("profile.uploading") : photo ? t("profile.change_photo") : t("profile.upload_photo")}
          </Button>
          {photo && (
            <Button
              data-testid="profile-remove-photo-btn"
              variant="outline"
              onClick={removePhoto}
              disabled={uploading}
              className="rounded-full border-[#C9C5B7] text-[#B5564B]"
            >
              <Trash2 size={16} strokeWidth={1.6} className="mr-1.5" />
              {t("profile.remove_photo")}
            </Button>
          )}
        </div>
        <Link
          to="/themes"
          data-testid="profile-presets-link"
          className="text-sm text-[#5F7161] hover:underline flex items-center gap-1.5"
        >
          <Palette size={15} strokeWidth={1.6} />
          {t("profile.choose_preset")}
        </Link>
      </section>

      {/* Nickname */}
      <section className="bg-white rounded-3xl border border-[#E6E2D8] p-6 space-y-3">
        <label className="text-sm font-medium">{t("profile.nickname_label")}</label>
        <div className="flex gap-2">
          <Input
            data-testid="profile-name-input"
            value={name}
            maxLength={30}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("profile.nickname_placeholder")}
            className="rounded-full"
          />
          <Button
            data-testid="profile-save-name-btn"
            onClick={saveName}
            disabled={savingName || !name.trim()}
            className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white shrink-0"
          >
            <Check size={16} strokeWidth={2} className="mr-1" />
            {t("profile.save")}
          </Button>
        </div>
        <p className="text-xs text-[#2C332A]/55">{t("profile.nickname_hint")}</p>
      </section>

      <section data-testid="profile-share-section">
        <ShareCard compact />
      </section>
    </div>
  );
}
